// src/pages/DuplicateScanPage.js
//
// "Scan for duplicates" (Coordinator page → Patient Flow, opens in a new tab).
// Lists every group of possible duplicate patients at the active site, on
// screen only: there is deliberately NO download (decided 2026-10-15: PHI
// stays inside RegX, and a list on screen can't go stale or get shared).
//
// Who is checked: the CRC page's patients (loadReportSearchPatients: consents
// with signedAt, plus the Google Sheet on Dubois). What counts: the portal's
// one same-patient rule, grouped by duplicateScan.js. Runs in the browser over
// data this user can already see; no patient record is ever changed here.
//
// "Not a duplicate" hides a group for everyone at the site
// (duplicateDismissalService.js: record ids + who/when only). Hidden groups are
// listed under "Hidden" with "Show again". A new record that matches one of
// them still shows up.
//
// "Move to Duplicates" on a row (2026-10-29) is the one action here that
// changes a patient: after a warning, a RegX patient moves to Potential
// Duplicates (whole record kept), and a Sheet patient's status becomes
// "Potential Duplicate" (duplicateMoveService.js). The chart restores either.
// The list is NOT re-scanned after a move: the moved person stays in their
// group, greyed out, so staff can move another person from the same group. A
// group leaves the list once only one person in it is left. "Scan again"
// starts fresh from the patient list (moved patients are no longer on it).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  CopyX,
  EyeOff,
  Eye,
  ExternalLink,
  Loader2,
  RefreshCw,
  Users,
} from "lucide-react";
import { moveToDuplicates, DuplicateMoveRefusedError } from "../services/duplicateMoveService";
import { useTenant } from "../context/TenantContext";
import { getActiveSiteId, activeSiteUsesLegacySheet } from "../services/activeTenant";
import { loadReportSearchPatients } from "../services/reportSearchPatients";
import {
  findDuplicateGroups,
  groupHeadline,
  groupMatchesFilter,
  GROUP_FILTERS,
  recordKey,
} from "../services/duplicateScan";
import { loadDismissals, dismissPairs, restorePairs } from "../services/duplicateDismissalService";
import { statusLabel } from "../constants/diseaseOptions";

const PAGE_SIZE = 50;
const HIDDEN = "hidden";

const btn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40";

const patientHref = (r) =>
  `/coordinator/patient/${encodeURIComponent(r.id)}?src=${r.source === "sheet" ? "sheet" : "firestore"}`;

const pairsOf = (g) => [...new Set(g.links.map((l) => l.pair).filter(Boolean))];

const fmtWhen = (at) => {
  const d = at && typeof at.toDate === "function" ? at.toDate() : at ? new Date(at) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
};

const DuplicateScanPage = () => {
  const navigate = useNavigate();
  const tenant = useTenant() || {};
  const siteId = tenant.siteId || getActiveSiteId();
  const siteName = ((tenant.sitesMeta || []).find((s) => s.id === siteId) || {}).name || siteId;

  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [dismissals, setDismissals] = useState(new Map());
  const [dismissError, setDismissError] = useState("");
  const [filter, setFilter] = useState("all");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [busyKey, setBusyKey] = useState("");
  const [actionError, setActionError] = useState("");
  // Row whose "Move to Duplicates" warning is open: { record, label, title }.
  const [moveTarget, setMoveTarget] = useState(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState("");
  const [moveDone, setMoveDone] = useState("");
  // Records moved to Duplicates since this scan (recordKey). Cleared by a scan.
  const [moved, setMoved] = useState(() => new Set());

  // Saved decisions. A failure never blocks the list: everything just shows.
  const refreshDismissals = useCallback(async () => {
    try {
      setDismissals(await loadDismissals());
      setDismissError("");
    } catch (err) {
      console.error("Couldn't load Not a duplicate decisions", err);
      setDismissError("Couldn't load which groups were marked \"Not a duplicate\", so every group is shown.");
    }
  }, []);

  const scan = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    setActionError("");
    try {
      const [{ patients, sheetError }] = await Promise.all([loadReportSearchPatients(), refreshDismissals()]);
      setState({ loading: false, error: "", data: { patients, sheetError, at: new Date() } });
      setMoved(new Set());
      setShown(PAGE_SIZE);
    } catch (err) {
      console.error("Duplicate scan failed", err);
      setState((s) => ({ ...s, loading: false, error: "Couldn't load the patient list. Try Scan again." }));
    }
  }, [refreshDismissals]);

  useEffect(() => {
    scan();
  }, [scan]);

  const data = state.data;
  const result = useMemo(
    () => (data ? findDuplicateGroups(data.patients, { dismissed: new Set(dismissals.keys()) }) : null),
    [data, dismissals]
  );

  const isMoved = useCallback((r) => moved.has(recordKey(r)), [moved]);
  const stillListed = useCallback((g) => g.records.filter((m) => !isMoved(m.record)).length, [isMoved]);
  // A group stays while two or more of its people haven't been moved.
  const groups = useMemo(
    () => (result ? result.groups.filter((g) => stillListed(g) >= 2) : []),
    [result, stillListed]
  );

  const showingHidden = filter === HIDDEN;
  const listed = useMemo(() => {
    if (!result) return [];
    if (showingHidden) return result.hiddenGroups;
    return groups.filter((g) => groupMatchesFilter(g, filter));
  }, [result, groups, filter, showingHidden]);
  const countFor = (key) => groups.filter((g) => groupMatchesFilter(g, key)).length;

  const save = async (pairs, key, hide, question) => {
    if (!pairs.length || !window.confirm(question)) return;
    setBusyKey(key);
    setActionError("");
    try {
      await (hide ? dismissPairs(pairs) : restorePairs(pairs));
      await refreshDismissals();
    } catch (err) {
      console.error("Couldn't save the decision", err);
      setActionError("Couldn't save that. Nothing was changed; try again.");
    } finally {
      setBusyKey("");
    }
  };

  const AFTER = `Nothing on the patient records changes, and you can show it again from "Hidden".`;

  // Whole group: every pair in it.
  const decideGroup = (g, key, hide) =>
    save(
      pairsOf(g),
      key,
      hide,
      hide
        ? `Mark ${g.records.length === 2 ? "these 2 records" : `all ${g.records.length} records`} as NOT the same person?\n\nThis group will be hidden for everyone at ${siteName}. ${AFTER}`
        : "Show this group in the list again, for everyone at this site?"
    );

  // One record in a group of 3+: only its links to the others, so the rest of
  // the group (the real duplicates) stays listed.
  const decideRecord = (g, m, key) =>
    save(
      [...new Set(g.links.filter((l) => l.a === m.label || l.b === m.label).map((l) => l.pair).filter(Boolean))],
      key,
      true,
      `Mark record ${m.label}${m.record.name ? ` (${m.record.name})` : ""} as NOT the same person as the others in this group?\n\nIt will be taken out of this group for everyone at ${siteName}; the other records stay listed. ${AFTER}`
    );

  const confirmMove = async () => {
    if (!moveTarget) return;
    const { record, label } = moveTarget;
    setMoving(true);
    setMoveError("");
    try {
      await moveToDuplicates(record);
      setMoveTarget(null);
      setMoveDone(`${record.name || `Record ${label}`} was moved to Potential Duplicates.`);
      // No re-scan: they stay greyed out in their group (see the header).
      setMoved((prev) => new Set(prev).add(recordKey(record)));
    } catch (err) {
      console.error("Move to Duplicates failed", err);
      setMoveError(
        err instanceof DuplicateMoveRefusedError
          ? err.message
          : "Couldn't move this patient. Scan again to check, and try again if they're still listed."
      );
    } finally {
      setMoving(false);
    }
  };

  const decidedBy = (g) => {
    const d = pairsOf(g)
      .map((p) => dismissals.get(p))
      .find(Boolean);
    return d ? `${d.dismissedBy || "someone"}${fmtWhen(d.dismissedAt) ? ` on ${fmtWhen(d.dismissedAt)}` : ""}` : "";
  };

  return (
    <div className="dup-scan-page min-h-screen bg-[#0b0820] text-slate-100 px-4 py-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button onClick={() => navigate("/coordinator")} className={`${btn} px-3 py-2 rounded-xl text-sm`}>
            <ArrowLeft className="w-4 h-4" />
            Coordinator
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold tracking-tight">Possible duplicate patients</h1>
            <p className="text-[12px] text-slate-400">
              Patients on the CRC page{activeSiteUsesLegacySheet() ? " and the Google Sheet" : ""} that may be the same
              person. Only <strong>Move to Duplicates</strong> changes a patient record.
            </p>
            <p className="text-[12px] text-slate-300 mt-0.5">
              Site: <span className="font-semibold">{siteName}</span>
            </p>
          </div>
          <button onClick={scan} disabled={state.loading} className={btn}>
            {state.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Scan again
          </button>
        </div>

        <div className="dup-scan-note mb-4 rounded-xl border p-3 text-[12px]">
          <p>
            <strong>How it decides:</strong> the same MRN; or the same date of birth and a matching name (nicknames,
            middle names and small typos count; the % is how closely the names match); or the same date of birth and an
            MRN one digit off. Real people can share a name and birthday, so please open and check each record before
            merging or removing anything. If a group is really different people, click <strong>Not a duplicate</strong>{" "}
            to hide it for everyone at this site. In a group of 3 or more, <strong>Not the same person</strong> on a row
            takes just that record out and leaves the rest. <strong>Move to Duplicates</strong> on a row moves that
            patient to Potential Duplicates; only do it once you're sure.
          </p>
        </div>

        {state.loading && !data && (
          <p className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning patients…
          </p>
        )}
        {state.error && <p className="py-4 text-sm text-rose-300">{state.error}</p>}

        {result && (
          <>
            {data.sheetError && (
              <div className="dup-scan-warn mb-3 flex items-start gap-2 rounded-xl border p-3 text-[12px]">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>The Google Sheet couldn't be read, so its patients weren't checked. Try Scan again.</span>
              </div>
            )}
            {dismissError && (
              <div className="dup-scan-warn mb-3 flex items-start gap-2 rounded-xl border p-3 text-[12px]">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{dismissError}</span>
              </div>
            )}
            {actionError && <p className="mb-3 text-[12px] text-rose-300">{actionError}</p>}
            {moveDone && (
              <p role="status" className="dup-scan-moved mb-3 text-[12px] text-emerald-300">
                {moveDone}
              </p>
            )}

            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-300">
              <span>
                <strong>{data.patients.length}</strong> patients checked
              </span>
              <span>
                <strong>{groups.length}</strong> possible duplicate groups
              </span>
              {result.unreadableDob + result.blankDob > 0 && (
                <span className="text-slate-400">
                  {result.unreadableDob + result.blankDob} with no readable date of birth (checked by MRN only)
                </span>
              )}
              <span className="text-slate-500">Scanned {data.at.toLocaleTimeString()}</span>
            </div>

            <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Filter by reason">
              {[...GROUP_FILTERS, { key: HIDDEN, label: "Hidden" }].map((f) => (
                <button
                  key={f.key}
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => {
                    setFilter(f.key);
                    setShown(PAGE_SIZE);
                  }}
                  className={`px-3 py-1.5 rounded-lg border text-[12px] transition ${
                    filter === f.key
                      ? "bg-purple-500/20 border-purple-300/40 text-white"
                      : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08]"
                  }`}
                >
                  {f.label} ({f.key === HIDDEN ? result.hiddenGroups.length : countFor(f.key)})
                </button>
              ))}
            </div>

            {showingHidden && listed.length > 0 && (
              <p className="mb-3 text-[12px] text-slate-400">
                Groups staff marked "Not a duplicate". They're left out of the list for everyone at this site.
              </p>
            )}

            {listed.length === 0 && (
              <p className="py-10 text-center text-sm text-slate-400">
                {showingHidden
                  ? "No groups are hidden."
                  : groups.length === 0
                  ? "No possible duplicates found."
                  : "No groups for this filter."}
              </p>
            )}

            <div className="space-y-3">
              {listed.slice(0, shown).map((g) => {
                const number = (showingHidden ? result.hiddenGroups : groups).indexOf(g) + 1;
                const left = showingHidden ? g.records.length : stillListed(g);
                const movedHere = g.records.length - left;
                const title = showingHidden ? `Hidden group ${number}` : `Group ${number}`;
                const key = pairsOf(g).join("|") || title;
                const canDecide = pairsOf(g).length > 0;
                return (
                  <section
                    key={key}
                    aria-label={title}
                    className="dup-scan-group rounded-xl border border-white/10 bg-white/[0.03] p-3"
                  >
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <Users className="w-4 h-4 text-purple-300" />
                      <h2 className="text-sm font-semibold text-white">{title}</h2>
                      <span className="dup-scan-pill rounded-md border px-1.5 py-0.5 text-[11px] font-semibold">
                        {groupHeadline(g)}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {g.records.length} records
                        {movedHere > 0 && ` · ${movedHere} moved to Duplicates`}
                      </span>
                      {showingHidden && decidedBy(g) && (
                        <span className="text-[11px] text-slate-400">· Marked not a duplicate by {decidedBy(g)}</span>
                      )}
                      {canDecide && (
                        <button
                          type="button"
                          onClick={() => decideGroup(g, key, !showingHidden)}
                          disabled={!!busyKey}
                          className={`${btn} ml-auto`}
                        >
                          {busyKey === key ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : showingHidden ? (
                            <Eye className="w-3.5 h-3.5" />
                          ) : (
                            <EyeOff className="w-3.5 h-3.5" />
                          )}
                          {showingHidden
                            ? "Show again"
                            : left > 2
                            ? "None are the same person"
                            : "Not a duplicate"}
                        </button>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[760px] text-left text-[12px]">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                            <th className="py-1 pr-2 font-semibold"></th>
                            <th className="py-1 pr-2 font-semibold">Name</th>
                            <th className="py-1 pr-2 font-semibold">DOB</th>
                            <th className="py-1 pr-2 font-semibold">MRN</th>
                            <th className="py-1 pr-2 font-semibold">Status</th>
                            <th className="py-1 pr-2 font-semibold">Why flagged</th>
                            <th className="py-1 font-semibold"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.records.map((m) => {
                            const r = m.record;
                            const wasMoved = !showingHidden && isMoved(r);
                            return (
                              <tr
                                key={`${r.source}:${r.id}`}
                                className={`border-t border-white/10 align-top ${wasMoved ? "dup-scan-moved-row opacity-50" : ""}`}
                              >
                                <td className="py-1.5 pr-2 font-semibold text-slate-300">{m.label}</td>
                                <td className="py-1.5 pr-2 text-white">{r.name || "—"}</td>
                                <td className="py-1.5 pr-2 text-slate-200">{r.dob || "—"}</td>
                                <td className="py-1.5 pr-2 text-slate-200">{r.mrn || "—"}</td>
                                <td className="py-1.5 pr-2 text-slate-300">
                                  {wasMoved ? "Moved to Duplicates" : statusLabel(r.status) || "—"}
                                </td>
                                <td className="py-1.5 pr-2 text-slate-300">{m.why.join("; ")}</td>
                                <td className="py-1.5 text-right whitespace-nowrap">
                                  <div className="inline-flex items-center gap-3">
                                    {/* Groups of 3+: take one record out, keep the rest. */}
                                    {!showingHidden && !wasMoved && left > 2 && (
                                      <button
                                        type="button"
                                        onClick={() => decideRecord(g, m, `${key}#${m.label}`)}
                                        disabled={!!busyKey}
                                        className="inline-flex items-center gap-1 text-slate-300 hover:text-white disabled:opacity-40"
                                        aria-label={`Record ${m.label} is not the same person (${title.toLowerCase()})`}
                                      >
                                        {busyKey === `${key}#${m.label}` ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <EyeOff className="w-3 h-3" />
                                        )}
                                        Not the same person
                                      </button>
                                    )}
                                    {r.id && (
                                      <a
                                        href={patientHref(r)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-purple-300 hover:text-white"
                                        aria-label={`Open record ${m.label} in ${title.toLowerCase()}`}
                                      >
                                        Open <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                    {!showingHidden && !wasMoved && r.id && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setMoveError("");
                                          setMoveDone("");
                                          setMoveTarget({ record: r, label: m.label, title });
                                        }}
                                        disabled={!!busyKey || moving}
                                        className="dup-scan-move inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 disabled:opacity-40"
                                        aria-label={`Move record ${m.label} to Duplicates (${title.toLowerCase()})`}
                                      >
                                        <CopyX className="w-3 h-3" />
                                        Move to Duplicates
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </div>

            {listed.length > shown && (
              <div className="mt-4 flex justify-center">
                <button onClick={() => setShown((n) => n + PAGE_SIZE)} className={btn}>
                  Show {Math.min(PAGE_SIZE, listed.length - shown)} more (of {listed.length - shown} left)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {moveTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Move to Duplicates"
          onKeyDown={(e) => {
            if (e.key === "Escape" && !moving) setMoveTarget(null);
          }}
        >
          <div className="dup-move-modal relative w-full max-w-md rounded-2xl border border-rose-400/40 bg-slate-950/95 p-5 shadow-[0_24px_60px_-12px_rgba(244,63,94,0.45)]">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-rose-500/20 border border-rose-400/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-[18px] h-[18px] text-rose-300" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white leading-tight">
                  Move {moveTarget.record.name || `record ${moveTarget.label}`} to Duplicates?
                </h3>
                <p className="text-[12px] text-slate-300 mt-1">
                  Record {moveTarget.label} in {moveTarget.title.toLowerCase()}
                  {moveTarget.record.mrn ? ` · MRN ${moveTarget.record.mrn}` : ""}
                  {moveTarget.record.dob ? ` · DOB ${moveTarget.record.dob}` : ""}
                </p>
                <p className="dup-move-warning mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 p-2.5 text-[12px] leading-relaxed text-rose-100">
                  <strong>Only do this if you are sure this is a duplicate.</strong> If you're unsure, open the chart
                  and check that every medical record and detail this patient has already exists on the other
                  patient, so no data is lost.
                </p>
                <p className="mt-2 text-[12px] leading-relaxed text-slate-300">
                  {moveTarget.record.source === "sheet"
                    ? "This patient's status becomes \"Potential Duplicate\". Their record in the Google Sheet is kept with all its data."
                    : "The whole record moves to Potential Duplicates, with all its data, notes and history."}{" "}
                  They leave the patient list and show in the Duplicates tab. To undo, open the chart and pick
                  another status. This group stays listed here until only one person in it is left.
                </p>
                {moveError && <p className="mt-2 text-[12px] text-rose-300">{moveError}</p>}
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <a
                href={patientHref(moveTarget.record)}
                target="_blank"
                rel="noopener noreferrer"
                className={`dup-move-open ${btn}`}
              >
                Open chart <ExternalLink className="w-3 h-3" />
              </a>
              <button
                type="button"
                onClick={() => setMoveTarget(null)}
                disabled={moving}
                className={`dup-move-cancel ${btn}`}
                autoFocus
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmMove}
                disabled={moving}
                className="dup-move-confirm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 ring-1 ring-white/20 text-[12px] font-semibold text-white disabled:opacity-50"
              >
                {moving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CopyX className="w-3.5 h-3.5" />}
                Move to Duplicates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateScanPage;
